import { useEffect, useState } from "react";
import { Bell, BellRing, Loader2, Smartphone } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";

const b64ToUint8 = (s) => {
  const pad = "=".repeat((4 - (s.length % 4)) % 4);
  const raw = atob((s + pad).replace(/-/g, "+").replace(/_/g, "/"));
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
};
const supported = () => "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
const isIOSBrowser = () => /iphone|ipad|ipod/i.test(navigator.userAgent) && !window.navigator.standalone;

export const PushNotificationsCard = () => {
  const [devices, setDevices] = useState(0);
  const [muted, setMuted] = useState(false);
  const [thisDevice, setThisDevice] = useState(false);
  const [perm, setPerm] = useState("default");
  const [busy, setBusy] = useState(false);
  const [ready, setReady] = useState(false);

  const refresh = async () => {
    try { const { data } = await api.get("/push/status"); setDevices(data.devices); setMuted(!!data.muted); } catch { /* ignore */ }
    if (supported()) {
      setPerm(Notification.permission);
      const reg = await navigator.serviceWorker.getRegistration();
      setThisDevice(!!(reg && (await reg.pushManager.getSubscription())));
    }
    setReady(true);
  };
  useEffect(() => { refresh(); }, []);

  const subscribeThisDevice = async () => {
    const reg = (await navigator.serviceWorker.getRegistration()) || (await navigator.serviceWorker.register("/sw.js"));
    await navigator.serviceWorker.ready;
    const p = await Notification.requestPermission();
    setPerm(p);
    if (p !== "granted") return false;
    const { data } = await api.get("/push/public-key");
    const sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: b64ToUint8(data.key) });
    await api.post("/push/subscribe", { ...sub.toJSON(), label: navigator.userAgent.slice(0, 80) });
    return true;
  };

  const active = devices > 0 && !muted;

  const toggle = async (on) => {
    setBusy(true);
    try {
      if (!on) {
        await api.put("/push/mute", { muted: true });
        toast.success("Notifiche in pausa. Riattivale quando vuoi da qui.");
      } else {
        if (!thisDevice && supported()) {
          const ok = await subscribeThisDevice();
          if (!ok && devices === 0) { toast.error("Il telefono ha bloccato le notifiche: tocca “Riprova” qui sotto."); return; }
        }
        await api.put("/push/mute", { muted: false });
        await api.post("/push/test");
        toast.success("Notifiche attive: ti abbiamo inviato un avviso di prova.");
      }
      await refresh();
    } catch (err) { toast.error("Operazione non riuscita: " + (err?.message || "errore")); }
    finally { setBusy(false); }
  };

  const addThisDevice = async () => {
    setBusy(true);
    try {
      const ok = await subscribeThisDevice();
      if (ok) { await api.put("/push/mute", { muted: false }); await api.post("/push/test"); toast.success("Questo dispositivo riceverà le notifiche."); }
      else toast.error("Permesso non concesso dal telefono.");
      await refresh();
    } catch (err) { toast.error("Non riuscito: " + (err?.message || "errore")); }
    finally { setBusy(false); }
  };

  return (
    <div className="rounded-2xl mo-card p-6" data-testid="push-card">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className={`h-10 w-10 rounded-xl flex items-center justify-center ${active ? "bg-[#5aa469] text-white" : "bg-gray-100 text-gray-500"}`}>
            {active ? <BellRing className="h-5 w-5" /> : <Bell className="h-5 w-5" />}
          </div>
          <div>
            <h3 className="font-display font-semibold text-xl">Notifiche sul telefono</h3>
            <p className="text-sm text-gray-500 mt-0.5 max-w-md">Avviso per ogni ordine, prenotazione da asporto e lista d'attesa, anche con l'app chiusa.</p>
            <p className="text-xs text-gray-400 mt-1" data-testid="push-devices">
              {devices > 0 ? `${devices} dispositiv${devices === 1 ? "o" : "i"} collegat${devices === 1 ? "o" : "i"}${muted ? " · in pausa" : ""}` : "Nessun dispositivo collegato"}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {busy && <Loader2 className="h-4 w-4 animate-spin text-gray-400" />}
          <Switch checked={active} disabled={busy || !ready} onCheckedChange={toggle} data-testid="push-toggle" />
        </div>
      </div>
      {ready && supported() && !thisDevice && perm !== "denied" && (
        <Button onClick={addThisDevice} disabled={busy} variant="outline" size="sm" className="mt-4 rounded-full border-gray-300 bg-white" data-testid="push-add-device-btn">
          <Smartphone className="h-4 w-4 mr-2" />{devices > 0 ? "Aggiungi anche questo dispositivo" : "Attiva su questo dispositivo"}
        </Button>
      )}
      {ready && supported() && perm === "denied" && !thisDevice && (
        <div className="mt-4 rounded-xl bg-amber-50 border border-amber-200 p-3 text-xs text-amber-800" data-testid="push-denied">
          Questo telefono ha bloccato le notifiche per MenuOne. Per sbloccarle: {isIOSBrowser() ? "Impostazioni iPhone → Notifiche → MenuOne → Consenti" : "tocca il lucchetto accanto all'indirizzo → Notifiche → Consenti"}, poi torna qui e premi
          <Button onClick={addThisDevice} disabled={busy} variant="link" className="h-auto p-0 pl-1 text-xs text-amber-900 underline" data-testid="push-retry-btn">Riprova</Button>.
        </div>
      )}
      {ready && !supported() && (
        <p className="mt-4 text-xs text-amber-700" data-testid="push-unsupported">{isIOSBrowser() ? "Su iPhone: installa prima l'app con “Scarica l'app”, poi attiva le notifiche da qui." : "Questo browser non supporta le notifiche push."}</p>
      )}
    </div>
  );
};
